import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  ActionRow,
  EmptyState,
  PageHeader,
  SkeletonTable,
} from "../../components/Directory/primitives";
import { formatIso, formatRelative } from "../../helpers/date";
import {
  listSupportMcpTokens,
  mintSupportMcpToken,
  revokeSupportMcpToken,
} from "./api";

/**
 * SCK-549 PR-6 — /access/support-mcp
 *
 * Caller-scoped token management for the Support MCP surface. Any
 * authenticated user can mint tokens for themselves; tokens are TTL-
 * capped at 90 days on coord and scoped to `/api/v1/mcp/*` only. The
 * page never shows the raw token value more than once — the mint
 * dialog copies it to the clipboard on demand and auto-dismisses after
 * 60 seconds; every list projection carries only `prefix` + `last4`.
 *
 * Sections:
 *   1. Create new token — inline form + "Mint token" primary CTA
 *   2. Your tokens — masked list with hover-reveal Revoke
 *   3. Expiry banner — reads coord's X-Token-Expires-In-Days header and
 *      warns when the smallest remaining TTL across the caller's tokens
 *      drops below EXPIRY_WARN_DAYS
 *
 * SOC 2 note (soc2_logging_customer_scope): the raw token never lands
 * in a console log, a toast, or a network error message — copy-failed
 * paths surface a generic "couldn't copy" hint and leave the plaintext
 * in the dialog for a manual selection.
 */

const EXPIRY_WARN_DAYS = 15;
const RAW_TOKEN_AUTO_DISMISS_MS = 60_000;
const TOKEN_TTL_DAYS = 90;

/**
 * Compose the exact CLI command customers paste after minting. Kept
 * verbatim across the dialog + the install snippet card so the two
 * places never drift.
 */
export function buildInstallSnippet(rawToken) {
  return `c2a mcp install support --token=${rawToken}`;
}

/**
 * Combine prefix + last4 into the compact `abc…7890` value the list
 * shows in the "Token" column. Returns "—" when the row is missing both
 * fields (older coord builds where the masked projection is thinner).
 */
export function formatMaskedToken(row) {
  if (!row) return "—";
  const prefix = row.prefix ?? "";
  const last4 = row.last4 ?? "";
  if (!prefix && !last4) return "—";
  return `${prefix}…${last4}`;
}

/**
 * Try to write `text` to the system clipboard. Uses the async API when
 * available and falls back to a hidden textarea + execCommand for older
 * browsers (Safari < 13 in particular). Resolves to a boolean so the
 * caller can render a "copied" affordance without needing to inspect
 * an exception it cannot log the token from.
 */
async function tryWriteClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // fall through to the textarea fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand?.("copy") === true;
    ta.remove();
    return ok;
  } catch (_) {
    return false;
  }
}

const LIST_COLUMNS = [
  { label: "Name" },
  { label: "Token", mono: true },
  { label: "Created" },
  { label: "Expires" },
  { label: "Last used" },
  { label: "", align: "right", width: 96 },
];

export default function SupportMcpTokensPage() {
  const [tokens, setTokens] = useState(null); // null = never loaded
  const [expiresInDays, setExpiresInDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newTokenName, setNewTokenName] = useState("");
  const [minting, setMinting] = useState(false);
  const [mintedToken, setMintedToken] = useState(null); // { name, token, prefix, last4, expiresAt }
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState(null); // { id, name } | null
  const [revoking, setRevoking] = useState(false);

  const [toast, setToast] = useState(null); // { severity, message }

  // Auto-dismiss the raw-token dialog so the plaintext never lingers on
  // the screen indefinitely. The mount-based timeout gets cleared when
  // the caller closes the dialog themselves.
  const dismissTimer = useRef(null);
  useEffect(() => {
    if (mintedToken) {
      dismissTimer.current = setTimeout(() => {
        setMintedToken(null);
        setCopied(false);
      }, RAW_TOKEN_AUTO_DISMISS_MS);
    }
    return () => {
      if (dismissTimer.current) {
        clearTimeout(dismissTimer.current);
        dismissTimer.current = null;
      }
    };
  }, [mintedToken]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { tokens: rows, expiresInDays: days } = await listSupportMcpTokens();
      setTokens(Array.isArray(rows) ? rows : []);
      setExpiresInDays(days);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const nameError = useMemo(() => {
    const v = newTokenName.trim();
    if (!v) return null; // don't nag the user until they try to submit
    if (v.length > 60) return "Keep the name under 60 characters.";
    return null;
  }, [newTokenName]);

  const canMint = newTokenName.trim().length > 0 && !nameError && !minting;

  const onMint = async () => {
    if (!canMint) return;
    setMinting(true);
    try {
      const res = await mintSupportMcpToken({ name: newTokenName.trim() });
      if (!res?.token) {
        // Coord should always echo the raw value on the mint response;
        // if it doesn't, we cannot let the user believe the token is
        // safely captured — surface an error instead of a silent success.
        throw new Error("Coordinator did not return a raw token value.");
      }
      setMintedToken(res);
      setNewTokenName("");
      setCopied(false);
      // Refresh in the background so the masked row appears in the list
      // by the time the dialog is dismissed.
      refresh();
    } catch (e) {
      // Never interpolate the token into the error — the mint attempt
      // may have partially succeeded server-side; guide the user to
      // reload rather than exposing raw wire body.
      const msg = (e?.message || "").slice(0, 200) || "Could not mint token.";
      setToast({ severity: "error", message: `Mint failed: ${msg}` });
    } finally {
      setMinting(false);
    }
  };

  const onCopyToken = async () => {
    if (!mintedToken?.token) return;
    const ok = await tryWriteClipboard(mintedToken.token);
    if (ok) {
      setCopied(true);
      setToast({
        severity: "success",
        message: "Token copied. It will not be shown again.",
      });
    } else {
      // Do NOT print the raw token into the error toast.
      setToast({
        severity: "warning",
        message: "Couldn't copy automatically. Select the token to copy it.",
      });
    }
  };

  const onCopySnippet = async () => {
    if (!mintedToken?.token) return;
    const ok = await tryWriteClipboard(buildInstallSnippet(mintedToken.token));
    setToast({
      severity: ok ? "success" : "warning",
      message: ok
        ? "Install command copied."
        : "Couldn't copy automatically. Select the command to copy it.",
    });
  };

  const onCloseMintedDialog = () => {
    setMintedToken(null);
    setCopied(false);
  };

  const onConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeSupportMcpToken(revokeTarget.id);
      setToast({
        severity: "success",
        message: `Revoked "${revokeTarget.name || "token"}".`,
      });
      setRevokeTarget(null);
      refresh();
    } catch (e) {
      const msg = (e?.message || "").slice(0, 200) || "Could not revoke token.";
      setToast({ severity: "error", message: `Revoke failed: ${msg}` });
    } finally {
      setRevoking(false);
    }
  };

  const rows = tokens || [];
  const showEmpty = !loading && !error && rows.length === 0;
  const showExpiryBanner =
    typeof expiresInDays === "number" &&
    expiresInDays >= 0 &&
    expiresInDays < EXPIRY_WARN_DAYS &&
    rows.length > 0;

  return (
    <Box data-testid="support-mcp-tokens-page">
      <PageHeader
        title="Support MCP Tokens"
        titleInfo="Mint per-user tokens so the Support MCP tools can be installed in Claude Desktop, Claude Code, or Cursor."
        subtitle={`Tokens are scoped to /api/v1/mcp/* only and expire after ${TOKEN_TTL_DAYS} days. The raw value is shown once at mint — copy it before closing the dialog.`}
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={loading}
            sx={{ textTransform: "none" }}
            data-testid="support-mcp-refresh"
          >
            Refresh
          </Button>
        }
      />

      {showExpiryBanner && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{ mb: 2 }}
          data-testid="support-mcp-expiry-banner"
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {expiresInDays === 0
              ? "A support MCP token expires today"
              : `A support MCP token expires in ${expiresInDays} day${expiresInDays === 1 ? "" : "s"}`}
          </AlertTitle>
          Mint a replacement below and update your MCP client before the
          old one lapses.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
          data-testid="support-mcp-error"
        >
          Couldn&apos;t load your tokens. {(error.message || "").slice(0, 200)}
        </Alert>
      )}

      {/* Section 1 — Create new token */}
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, mb: 3 }}
        data-testid="support-mcp-mint-section"
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Create a new token
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, maxWidth: 720 }}
        >
          Give the token a human-readable name so you can identify it
          later (for example: <em>laptop-claude-desktop</em>). The raw
          value is displayed once, then only its prefix and last four
          characters are retained.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "flex-start" }}
        >
          <TextField
            size="small"
            label="Token name"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canMint) onMint();
            }}
            error={!!nameError}
            helperText={nameError || " "}
            sx={{ minWidth: 280, flex: 1 }}
            inputProps={{
              maxLength: 80,
              "data-testid": "support-mcp-name-input",
              "aria-label": "Token name",
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onMint}
            disabled={!canMint}
            sx={{ textTransform: "none", mt: { xs: 0, sm: 0.5 } }}
            data-testid="support-mcp-mint-button"
          >
            {minting ? "Minting…" : "Mint token"}
          </Button>
        </Stack>
      </Paper>

      {/* Section 2 — Your tokens */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Your tokens
      </Typography>

      {loading && !tokens && (
        <SkeletonTable columns={LIST_COLUMNS} rows={4} />
      )}

      {showEmpty && (
        <EmptyState
          icon={<KeyOutlinedIcon />}
          title="No support MCP tokens yet"
          description="Mint your first token above to install the Support MCP surface in your editor of choice."
          variant="empty"
        />
      )}

      {!loading && !showEmpty && rows.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <Table size="small" aria-label="Support MCP tokens">
            <TableHead>
              <TableRow>
                {LIST_COLUMNS.map((c) => (
                  <TableCell
                    key={c.label || "actions"}
                    align={c.align || "left"}
                    sx={{
                      ...(c.width ? { width: c.width } : null),
                      ...(c.mono ? { fontFamily: "monospace" } : null),
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <ActionRow
                  key={row.id}
                  data-testid="support-mcp-token-row"
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.name || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {formatMaskedToken(row)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatIso(row.createdAt) || ""}>
                      <span>{formatRelative(row.createdAt)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatIso(row.expiresAt) || ""}>
                      <span>{formatRelative(row.expiresAt)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.lastUsedAt ? (
                      <Tooltip title={formatIso(row.lastUsedAt) || ""}>
                        <span>{formatRelative(row.lastUsedAt)}</span>
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="never"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box className="row-actions">
                      <Tooltip title="Revoke this token">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setRevokeTarget({ id: row.id, name: row.name })
                          }
                          aria-label={`Revoke token ${row.name || row.id}`}
                          data-testid="support-mcp-revoke-button"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </ActionRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Minted-token dialog — raw value shown once */}
      <Dialog
        open={!!mintedToken}
        onClose={onCloseMintedDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="support-mcp-token-shown-title"
        data-testid="support-mcp-token-shown-dialog"
      >
        <DialogTitle id="support-mcp-token-shown-title" sx={{ pb: 0.5 }}>
          Copy your token now
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This is the only time the raw token is shown. Copy it now —
            when this dialog closes the platform retains only the prefix
            and last four characters. This dialog auto-dismisses after
            60&nbsp;seconds.
          </DialogContentText>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
          >
            Raw token
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              fontFamily: "monospace",
              fontSize: 13,
              wordBreak: "break-all",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.03)",
              position: "relative",
            }}
          >
            <Box
              component="span"
              data-testid="support-mcp-raw-token"
              sx={{ pr: 5 }}
            >
              {mintedToken?.token}
            </Box>
            <Tooltip title={copied ? "Copied" : "Copy token"}>
              <IconButton
                size="small"
                onClick={onCopyToken}
                aria-label="Copy raw token"
                data-testid="support-mcp-copy-token"
                sx={{ position: "absolute", top: 6, right: 6 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>

          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
          >
            Install command
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              fontFamily: "monospace",
              fontSize: 13,
              wordBreak: "break-all",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.03)",
              position: "relative",
            }}
          >
            <Box
              component="span"
              data-testid="support-mcp-install-snippet"
              sx={{ pr: 5 }}
            >
              {mintedToken?.token
                ? buildInstallSnippet(mintedToken.token)
                : ""}
            </Box>
            <Tooltip title="Copy install command">
              <IconButton
                size="small"
                onClick={onCopySnippet}
                aria-label="Copy install command"
                data-testid="support-mcp-copy-snippet"
                sx={{ position: "absolute", top: 6, right: 6 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onCloseMintedDialog}
            variant="contained"
            sx={{ textTransform: "none" }}
            data-testid="support-mcp-token-dismiss"
          >
            I&apos;ve copied it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => (!revoking ? setRevokeTarget(null) : null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="support-mcp-revoke-title"
      >
        <DialogTitle id="support-mcp-revoke-title">Revoke token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Any MCP client still using{" "}
            <strong>{revokeTarget?.name || "this token"}</strong> will stop
            working immediately. Mint a replacement first if you need
            uninterrupted access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevokeTarget(null)}
            disabled={revoking}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmRevoke}
            color="error"
            variant="contained"
            disabled={revoking}
            sx={{ textTransform: "none" }}
            data-testid="support-mcp-revoke-confirm"
          >
            {revoking ? "Revoking…" : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
